import { Box, Button, TextField, Typography, Autocomplete, Chip, Modal, Card, Tooltip, Select, MenuItem, Input, Tabs, Tab, InputAdornment } from "@mui/material";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useCreateCollection, useUpdateCollection, sharedCollectionFieldDefaults } from "./hooks";
import { useProductTypeQuery } from "../ProductTypes/hooks";
import ChildCollectionModal from "./ChildCollectionModal";
import { SearchOptionsInput } from "./SearchOptionsInput";
import { SortAndFilterInput } from "./SortAndFilterInput";
import { ContentPreview } from "components/Content/ContentPreview";
import { COLORS } from "constants/colors";
import { ImagePreviewInput } from "components/ImagePreviewInput";
import { NumberField } from "@base-ui-components/react/number-field";
import DirectReferencesSection from "./directReferencesSection";

export const defaultFormData = {
  ...sharedCollectionFieldDefaults,
  appIcon: "",
  webCutoutImage: "",
  webBackgroundImage: "",
  searchTerms: "",
  pageContent: "",
  productTypes: [],
  children: [],
  internalNotes: "",
  directReferences: []
};

const GenieCollectionsForm = React.memo(({ collection, onSave }) => {
  const [showForm, setShowForm] = useState(!!collection);
  const [formData, setFormData] = useState(
    collection || defaultFormData
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const createCollection = useCreateCollection();
  const updateCollection = useUpdateCollection();
  const { data: productTypesData } = useProductTypeQuery();
  const productTypes = useMemo(() => productTypesData?.productTypes || [], [productTypesData]);

  const [sortAndFilter, setSortAndFilter] = useState({});

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = useCallback((e) => {
    e.preventDefault();

    const searchOptions = {
      ...(typeof formData.searchOptions === "string"
        ? JSON.parse(formData.searchOptions)
        : formData.searchOptions),
      sortAndFilter
    };

    // Transform data to match the expected API format
    const formattedData = {
      ...formData,
      productTypeIds: formData.productTypes.map(pt =>
        typeof pt === "object" ? Number(pt.id) : Number(pt)
      ),
      searchOptions,
      displayOptions: formData.displayOptions || {},
      ParentGenieCollectionMeta: formData.children.map(child => {
        const childId = child.id === "" ? null : child.id ? Number(child.id) : null;
        const childGenieId = child.childGenieCollectionId === ""
          ? null
          : child.childGenieCollectionId ? Number(child.childGenieCollectionId) : null;

        return {
          id: childId,
          slug: child.slug || defaultFormData.slug,
          displayName: child.displayName || defaultFormData.displayName,
          description: child.description || defaultFormData.description,
          overline: child.overline || defaultFormData.overline,
          underline: child.underline || defaultFormData.underline,
          cta: child.cta || defaultFormData.cta,
          searchOptions: child.searchOptions || defaultFormData.searchOptions,
          childGenieCollectionId: childGenieId,
          ChildGenieCollection: {
            id: childGenieId
          },
          order: Number(child.order) || defaultFormData.order,
          displayOptions: child.displayOptions || defaultFormData.displayOptions
        };
      })
    };

    // Add direct references if using that mode
    if (formData.directReferences.length > 0) {
      formattedData.directReferences = formData.directReferences;
    }
    if (formData.searchTerms) {
      formattedData.searchTerms = formData.searchTerms;
    }
    if (formData.productTypes.length > 0) {
      formattedData.productTypeIds = formData.productTypes.map(pt =>
        typeof pt === "object" ? Number(pt.id) : Number(pt)
      );
    }

    if (collection) {
      updateCollection.mutate({ id: Number(collection.id), updates: formattedData }, {
        onSuccess: (updatedCollection) => onSave(updatedCollection.id)
      });
    } else {
      createCollection.mutate(formattedData, {
        onSuccess: (newCollection) => onSave(newCollection.id)
      });
    }
  }, [formData, collection, updateCollection, createCollection, onSave, sortAndFilter]
  );

  // Transform ParentGenieCollectionMeta into children array format
  const mapChildCollections = useCallback((collection) => {
    if (!collection) { return []; }

    const childrenArray = collection.ParentGenieCollectionMeta?.map(meta => {
      return {
        id: meta.id ? Number(meta.id) : null,
        order: Number(meta.order) || 0,
        slug: meta.slug || meta.ChildGenieCollection?.slug || "",
        displayName: meta.displayName || meta.ChildGenieCollection?.displayName || "",
        description: meta.description || "",
        overline: meta.overline || "",
        underline: meta.underline || "",
        cta: meta.cta || "",
        displayOptions: meta.displayOptions || {},
        searchOptions: meta.searchOptions || {},
        childGenieCollectionId: meta.childGenieCollectionId
          ? Number(meta.childGenieCollectionId)
          : (meta.ChildGenieCollection?.id ? Number(meta.ChildGenieCollection.id) : null)
      };
    }) || [];

    return childrenArray;
  }, []);

  // Map selected product types
  const mapSelectedProductTypes = useCallback((collection) => {
    if (!collection || !collection.productTypes) { return []; }

    return collection.productTypes.map(pt => ({
      id: Number(pt.id),
      name: pt.name
    }));
  }, []);

  const handleCancel = useCallback(() => {
    if (collection) {
      setFormData({
        ...collection,
        productTypes: mapSelectedProductTypes(collection),
        children: mapChildCollections(collection),
        directReferences: collection.directReferences || []
      });
    } else {
      setShowForm(false);
    }

    onSave(collection?.id);
  }, [collection, mapChildCollections, mapSelectedProductTypes, onSave]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [editingChildIndex, setEditingChildIndex] = useState(null);

  const handleAddChild = useCallback(() => {
    setEditingChild(null);
    setEditingChildIndex(null);
    setIsModalOpen(true);
  }, []);

  const handleEditChild = useCallback((child, index) => {
    setEditingChild({ ...child });
    setEditingChildIndex(index);
    // Use setTimeout to ensure editingChild is set before opening modal
    setTimeout(() => setIsModalOpen(true), 0);
  }, []);

  const handleSaveChild = useCallback((child) => {
    setFormData((prev) => {
      if (editingChildIndex !== null) {
        const newChildren = [...prev.children];
        newChildren[editingChildIndex] = { ...child };
        return {
          ...prev,
          children: newChildren
        };
      } else {
        return {
          ...prev,
          children: [...prev.children, { ...child }]
        };
      }
    });

    setIsModalOpen(false);
  }, [editingChildIndex]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingChild(null);
    setEditingChildIndex(null);
  }, []);

  const updateFormData = useCallback((data) => {
    const newFormData = { ...formData, ...data };
    setFormData(newFormData);
  }, [formData]);

  useEffect(() => {
    setShowForm(!!collection);
    if (collection) {
      const searchOptions = typeof collection.searchOptions === "object"
        ? JSON.stringify(collection.searchOptions)
        : collection.searchOptions || "";

      const searchOptionsObject = typeof searchOptions === "string"
        ? JSON.parse(searchOptions)
        : searchOptions || {};

      const sortAndFilter = searchOptionsObject?.sortAndFilter || null;
      setSortAndFilter(sortAndFilter);

      setFormData({
        ...collection,
        searchOptions,
        productTypes: mapSelectedProductTypes(collection),
        children: mapChildCollections(collection),
        displayOptions: collection.displayOptions || {},
        directReferences: collection.directReferences || []
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [collection, mapSelectedProductTypes, mapChildCollections]);

  if (!showForm && !collection) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", m: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => setShowForm(true)}
        >
          Create New Collection
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSave} sx={{ bgcolor: "background.paper", borderRadius: 2, boxShadow: 3, p: 3 }}>
      <Box>
        <Typography variant="h6">Details for collection id {formData.id}</Typography>
        <Tooltip
          title="Displays in carousel if standalone and on deeplink page"
          placement="top"
          arrow
        >
          <TextField
            fullWidth
            margin="normal"
            label="Display Name"
            name="displayName"
            value={formData.displayName || ""}
            onChange={handleChange}
            required
          /></Tooltip>
        <TextField
          fullWidth
          margin="normal"
          multiline
          rows={2}
          label="Internal Notes - optional, for internal use only"
          name="internalNotes"
          value={formData.internalNotes || ""}
          onChange={handleChange}
        />
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
          <Box mt={3}>
            <Tooltip
              title="If checked, this collection will be displayed on the brands tab in the app and in the nav bar and footer on web"
              placement="top"
              arrow
            >
              <Typography variant="h6">Primary?</Typography>
            </Tooltip>
            <Input
              type="checkbox"
              name="isPrimary"
              checked={Boolean(formData.isPrimary)}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isPrimary: e.target.checked
                }))
              }
            />
          </Box>
          <Tooltip
            title="This is the slug that will be used to identify this collection in the URL. It must be unique and cannot contain spaces or special characters. It will be used to generate the deeplink for the collection."
            placement="top"
            arrow
          >
            <TextField
              fullWidth
              margin="normal"
              label="Slug"
              name="slug"
              value={formData.slug || ""}
              onChange={handleChange}
              required
            /></Tooltip>
          <Box>
            <Tooltip
              title="Live/New/Coming Soon are displayed in the app/on web. Hidden is not displayed in the app. New is display before Live, Coming Soon is display after Live."
              placement="top"
              arrow
            >
              <Typography variant="h6">Status</Typography>
            </Tooltip>
            <Select
              name="status"
              value={formData.status || "live"}
              label="Status"

              onChange={handleChange}
            >
              {["live", "hidden", "new", "coming_soon"].map((status, i) => (
                <MenuItem key={i} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        {formData?.isPrimary && (
          <Box sx={{ boxShadow: 3, borderRadius: 2, p: 2, bgcolor: "background.paper", mt: 2 }}>
            <Typography variant="h5">Primary Collection Details</Typography>
            <Tooltip
              title="Determines order in navCutout/web footer within New/Live/Coming Soon. Lower numbers are displayed first."
              placement="top"
              arrow
            >
              <Typography variant="h6" sx={{ mt: 2 }}>Nav List Order</Typography>
            </Tooltip>
            <NumberField.Root>
              <NumberField.ScrubArea>
                <NumberField.Input
                  style={{
                    borderRadius: "4px",
                    padding: "12px",
                    borderColor: COLORS.LIGHT_GRAY
                  }}
                  name="Order"
                  onChange={handleChange}
                  value={
                    formData.order
                  }
                />
              </NumberField.ScrubArea>
            </NumberField.Root>

            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mt: 2, flexWrap: "wrap" }}>
              <Box>
                <Tooltip
                  title="Displays on brands tab in app"
                  placement="top"
                  arrow
                >
                  <Typography variant="h6">App Icon</Typography>
                </Tooltip>
                <ImagePreviewInput
                  alt={formData?.appIcon ?? formData?.appIcon}
                  name="appIcon"
                  src={formData?.appIcon}
                  onSrcChange={handleChange}
                  sx={{
                    width: "20vw",
                    mb: 2
                  }}
                />
              </Box>
              <Box>
                <Tooltip
                  title="Displays in navbar/footer on web"
                  placement="top"
                  arrow
                >
                  <Typography variant="h6">Web Cutout Image</Typography>
                </Tooltip>
                <ImagePreviewInput
                  alt={formData?.webCutoutImage ?? formData?.webCutoutImage}
                  name="webCutoutImage"
                  src={formData?.webCutoutImage}
                  onSrcChange={handleChange}
                  sx={{
                    width: "20vw",
                    mb: 2
                  }}
                />
              </Box>
              <Box>
                <Tooltip
                  title="Displays in navbar/footer on web"
                  placement="top"
                  arrow
                >
                  <Typography variant="h6">Web Background Image</Typography>
                </Tooltip>
                <ImagePreviewInput
                  alt={formData?.webBackgroundImage ?? formData?.webBackgroundImage}
                  name="webBackgroundImage"
                  src={formData?.webBackgroundImage}
                  onSrcChange={handleChange}
                  sx={{
                    width: "20vw",
                    mb: 2
                  }}
                />
              </Box>
              <Tooltip
                title="Drives text in web navbar. Defaults to displayName if not present"
                placement="top"
                arrow
              >
                <Typography variant="h6">Web Nav Display Name</Typography>
                <TextField
                  margin="dense"
                  name="displayOptions.webNavDisplayName"
                  fullWidth
                  placeholder={formData.displayName}
                  value={formData.displayOptions?.webNavDisplayName || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      displayOptions: {
                        ...prev.displayOptions,
                        webNavDisplayName: e.target.value
                      }
                    }))
                  }
                />
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", mt: 2, flexDirection: "row", gap: 2, alignItems: "flex-start" }}>
              <Tooltip
                title="Drives color of web navbar active underline and 'new' pill"
                placement="top"
                arrow
              >
                <Typography variant="h6">Web Primary Color</Typography>
                <TextField
                  margin="dense"
                  label="format: #FFFFFF"
                  name="displayOptions.primaryColor"
                  value={formData.displayOptions?.primaryColor || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      displayOptions: {
                        ...prev.displayOptions,
                        primaryColor: e.target.value
                      }
                    }))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            border: "1px solid #ccc",
                            backgroundColor: formData.displayOptions?.primaryColor || "#ffffff"
                          }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Tooltip>
              <Box sx={{ flexDirection: "column", gap: 2 }}>
                <Tooltip
                  title="Additional colors for border around featured brands (collectionId 76) on genie home when this collection is status: new - format: #FFFFFF"
                  placement="top"
                  arrow
                >
                  <Typography variant="h6">Web Secondary Colors</Typography>
                </Tooltip>
                {(formData.displayOptions?.secondaryColors || []).map((color, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                    <TextField
                      margin="normal"
                      label={`Secondary Color ${index + 1} format: #FFFFFF`}
                      fullWidth
                      value={color}
                      onChange={(e) => {
                        const newColors = [...(formData.displayOptions?.secondaryColors || [])];
                        newColors[index] = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          displayOptions: {
                            ...prev.displayOptions,
                            secondaryColors: newColors
                          }
                        }));
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                border: "1px solid #ccc",
                                backgroundColor: color || "#ffffff"
                              }}
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const newColors = (formData.displayOptions?.secondaryColors || []).filter((_, i) => i !== index);
                        setFormData((prev) => ({
                          ...prev,
                          displayOptions: {
                            ...prev.displayOptions,
                            secondaryColors: newColors
                          }
                        }));
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    const newColors = [...(formData.displayOptions?.secondaryColors || []), ""];
                    setFormData((prev) => ({
                      ...prev,
                      displayOptions: {
                        ...prev.displayOptions,
                        secondaryColors: newColors
                      }
                    }));
                  }}
                >
                  Add Secondary Color
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <Box sx={{ boxShadow: 3, borderRadius: 2, p: 2, bgcolor: "background.paper", mt: 2 }}>
          <Tooltip
            title="When referenced as a standalone, this section dictates how this collection will appear as it's own carousel"
            placement="top"
            arrow
          >
            <Typography variant="h6">Carousel display content</Typography>
          </Tooltip>
          <TextField
            fullWidth
            margin="normal"
            label="Overline - optional"
            name="overline"
            value={formData.overline || ""}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description - optional"
            name="description"
            value={formData.description || ""}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Underline - optional"
            name="underline"
            value={formData.underline || ""}
            onChange={handleChange}
          />
          <Tooltip
            title="When CTA is set, this will be the link to the search results page. Note: search will only work if searchTerms driven, not productType driven"
            placement="top"
            arrow
          >
            <TextField
              fullWidth
              margin="normal"
              label="CTA - optional, dictates link to search results page"
              name="cta"
              value={formData.cta || ""}
              onChange={handleChange}
            />
          </Tooltip>
          <Tooltip
            title="Click any button to start configuring that type. You can configure multiple types together."
            placement="top"
            arrow
          >
            <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
              <Typography variant="h6">Results Configuration</Typography>

              {/* Main tabs for configuration */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                  <Tab label="Search Terms" />
                  <Tab label="Product Types" />
                  <Tab label="Direct References" />
                </Tabs>
              </Box>

              {/* Tab content */}
              {activeTab === 0 && (
                <Box sx={{ p: 2, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Search Terms Configuration
                  </Typography>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Search Terms"
                    value={formData.searchTerms || ""}
                    onChange={(e) =>
                      updateFormData({
                        searchTerms: e.target.value
                      })
                    }
                    placeholder="Enter search terms"
                    multiline
                    rows={2}
                  />
                  <SearchOptionsInput
                    searchOptions={typeof formData.searchOptions === "string" ? (formData.searchOptions ? JSON.parse(formData.searchOptions) : {}) : formData.searchOptions || {}}
                    onChange={(newOptions) =>
                      setFormData((prev) => ({
                        ...prev,
                        searchOptions: newOptions
                      }))
                    }
                  />
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ p: 2, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Product Types Configuration
                  </Typography>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={productTypes}
                    getOptionLabel={(option) => typeof option === "object" ? option.name : ""}
                    value={formData.productTypes}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        productTypes: newValue
                      }))
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Product Types"
                        placeholder="Select product types"
                        margin="normal"
                        fullWidth
                      />
                    )}
                  />
                  <SearchOptionsInput
                    searchOptions={typeof formData.searchOptions === "string" ? (formData.searchOptions ? JSON.parse(formData.searchOptions) : {}) : formData.searchOptions || {}}
                    onChange={(newOptions) =>
                      setFormData((prev) => ({
                        ...prev,
                        searchOptions: newOptions
                      }))
                    }
                  />
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ p: 2, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Direct References Configuration
                  </Typography>
                  <DirectReferencesSection
                    directReferences={formData.directReferences}
                    onChange={(directReferences) =>
                      setFormData((prev) => ({
                        ...prev,
                        directReferences
                      }))
                    }
                    searchTerms={formData.searchTerms || ""}
                    productTypes={productTypes}
                    onSearchTermsChange={(searchTerms) =>
                      setFormData((prev) => ({
                        ...prev,
                        searchTerms
                      }))
                    }
                    onProductTypesChange={(productTypes) =>
                      setFormData((prev) => ({
                        ...prev,
                        productTypes
                      }))
                    }
                    searchOptions={typeof formData.searchOptions === "string" ? (formData.searchOptions ? JSON.parse(formData.searchOptions) : {}) : formData.searchOptions || {}}
                    onSearchOptionsChange={(newOptions) =>
                      setFormData((prev) => ({
                        ...prev,
                        searchOptions: newOptions
                      }))
                    }
                  />
                  <SearchOptionsInput
                    searchOptions={typeof formData.searchOptions === "string" ? (formData.searchOptions ? JSON.parse(formData.searchOptions) : {}) : formData.searchOptions || {}}
                    onChange={(newOptions) =>
                      setFormData((prev) => ({
                        ...prev,
                        searchOptions: newOptions
                      }))
                    }
                  />
                </Box>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box >
      <SortAndFilterInput
        sortAndFilterOptions={sortAndFilter}
        onChange={(newSortAndFilterOptions) =>
          setSortAndFilter(newSortAndFilterOptions)
        }
      />
      <Box>
        <Box sx={{ boxShadow: 3, borderRadius: 2, p: 2, bgcolor: "background.paper", mt: 2 }}>
          <Tooltip
            title="If set and if this collection is referenced as a child collection of another collection, this collection will be displayed in a nav cutout carousel on the parent collection page."
            placement="top"
            arrow
          >
            <Typography variant="h6">Page Display Content</Typography>
          </Tooltip>
          <TextField
            fullWidth
            margin="normal"
            label="Page Content"
            name="pageContent"
            multiline
            rows={4}
            value={formData.pageContent || ""}
            onChange={handleChange}
          />
          <Button
            sx={{ mt: 2, mb: 2 }}
            variant="contained"
            onClick={() => {
              setPreviewOpen(!previewOpen);
            }}
          >
            Preview page content
          </Button>
          {formData?.isPrimary && (
            <Tooltip
              title="App and web are just different enough that we have to have two different fields for the description."
              placement="top"
              arrow
            >
              <TextField
                margin="normal"
                fullWidth
                rows={4}
                multiline
                label="Web Page Description"
                name="displayOptions.webpageDescription"
                value={formData.displayOptions?.webpageDescription || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    displayOptions: {
                      ...prev.displayOptions,
                      webpageDescription: e.target.value
                    }
                  }))
                }
              />
            </Tooltip>
          )}
          <Typography variant="h6" mt={2}>Child Collection Carousels</Typography>
          <ChildCollectionModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveChild}
            child={editingChild}
          />
          <Box component="ul" sx={{ pl: 2 }}>
            {(formData.children || []).sort((a, b) => a.order - b.order).map((child, index) => (
              <Box
                component="li"
                key={index}
                sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <Typography variant="body4" color="text.secondary">Order: {child.order}</Typography>
                <Typography>{child.displayName}</Typography>
                <Button variant="outlined" size="small" onClick={() => handleEditChild(child, index)}>
                  Edit
                </Button>
              </Box>
            ))}
          </Box>
          <Button variant="contained" onClick={handleAddChild} sx={{ mt: 2 }}>
            Add Child
          </Button>
        </Box>
      </Box>
      <Box mt={4} display="flex" justifyContent="space-between">
        <Button variant="contained" color="primary" type="submit">
          Save
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
      <Modal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
        }}
      >
        <Box
          height="100vh"
          width="100vw"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => {
            setPreviewOpen(false);
          }}
          sx={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <Card
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{
              borderRadius: 4,
              width: "95vw",
              height: "90vh",
              maxWidth: "none",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box
              sx={{
                background: COLORS.PERIWINKLE,
                padding: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Typography variant="h4" fontWeight="bold" color={COLORS.WHITE}>
                Content Editor & Preview
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setPreviewOpen(false)}
              >
                Close
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                height: "calc(90vh - 64px)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  borderRight: `1px solid ${COLORS.LIGHT_GRAY}`,
                  padding: 2
                }}
              >
                <TextField
                  label="Page Content"
                  name="pageContent"
                  multiline
                  fullWidth
                  value={formData.pageContent}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({
                      ...prev,
                      pageContent: newValue
                    }))
                  }
                  variant="outlined"
                  sx={{
                    height: "calc(100% - 16px)",
                    "& .MuiInputBase-root": {
                      height: "100%"
                    },
                    "& .MuiInputBase-input": {
                      height: "100% !important",
                      overflow: "auto !important"
                    }
                  }}
                  InputProps={{
                    style: {
                      height: "100%"
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  overflowY: "auto",
                  bgcolor: "#f9f9f9",
                  padding: 4
                }}
              >
                <ContentPreview

                  body={formData.pageContent}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Modal>
    </Box >
  );
});

// Add display name to fix the linter warning
GenieCollectionsForm.displayName = "GenieCollectionsForm";

export default GenieCollectionsForm;
